/**
 * Integer query-string parameters.
 *
 * Every integer a booking hand-off link can carry — a trip id, a reservation
 * id, a night count — goes through one of these two, so a malformed value can
 * never reach a reservation request as `NaN`.
 */

/**
 * Parse a positive-integer query param.
 *
 * Anything that isn't a positive integer (`null`, empty string, `'abc'`,
 * `'557.5'`, `'0'`, `'-1'`) yields `null`, so callers can fall back: to the
 * session's reservation, or — for a trip id — to the no-trip landing screen
 * rather than issuing a doomed reservation request.
 *
 * Use {@link parseNonNegativeIntParam} for the one param where `0` is a real
 * answer rather than a malformed one.
 */
export function parsePositiveIntParam(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n <= 0) return null;
  return n;
}

/**
 * {@link parsePositiveIntParam}, but `0` counts.
 *
 * For `?extendableHotelPhaseNights=`, where zero extra hotel nights is a
 * meaningful request and not a malformed one. Everything else that guard rejects
 * — a fraction, a negative, `NaN` from `Number('abc')` — still yields `null`, so
 * nothing unreadable leaks into the autoreserve mutation.
 */
export function parseNonNegativeIntParam(value: string | null | undefined): number | null {
  if (value == null || value === '') return null;
  const n = Number(value);
  if (!Number.isInteger(n) || n < 0) return null;
  return n;
}
